import { fixedUi } from '@/i18n/fixedUi';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { platformAPI, getIpeWorkspaceMode } from '@/utils/api';
import { showToast } from '@/utils/toast';
import {
  TEMU_ACTIVE_SHOP_STORAGE_KEY,
  isTemuShopAuthorized,
  normalizeTemuShopType,
  shopTypeLabelForSlug as labelForTemuShopTypeSlug,
} from '@/temu-platform/constants/temuRoutes';
import { useTemuWorkspaceAuthOptional } from '@/temu-platform/context/TemuWorkspaceAuthContext';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';

const TemuShopContext = createContext(null);

function readStoredShopId() {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(TEMU_ACTIVE_SHOP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function readUrlShopId() {
  if (typeof window === 'undefined') return '';
  try {
    const id = new URLSearchParams(window.location.search).get('shopId');
    return isConcreteShopId(id) ? String(id) : '';
  } catch {
    return '';
  }
}

function persistShopId(shopId) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (shopId) {
      localStorage.setItem(TEMU_ACTIVE_SHOP_STORAGE_KEY, String(shopId));
    } else {
      localStorage.removeItem(TEMU_ACTIVE_SHOP_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function shopTypeEqualsTemu(a, b) {
  return normalizeTemuShopType(a) === normalizeTemuShopType(b);
}

function useWorkspaceModeKey() {
  const [mode, setMode] = useState(() => getIpeWorkspaceMode());
  useEffect(() => {
    const sync = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setMode(next);
        return;
      }
      setMode(getIpeWorkspaceMode());
    };
    window.addEventListener('ipe:workspace-mode-changed', sync);
    window.addEventListener('ipe-workspace-mode-changed', sync);
    window.addEventListener('ipe:workspace-refresh', sync);
    return () => {
      window.removeEventListener('ipe:workspace-mode-changed', sync);
      window.removeEventListener('ipe-workspace-mode-changed', sync);
      window.removeEventListener('ipe:workspace-refresh', sync);
    };
  }, []);
  return mode;
}

export function TemuShopProvider({ children }) {
  const { shopType: shopTypeSlug = 'semi' } = useParams();
  const workspaceAuth = useTemuWorkspaceAuthOptional();
  const workspaceMode = useWorkspaceModeKey();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeShopId, setActiveShopId] = useState(() => readUrlShopId() || readStoredShopId());

  const expectedType = useMemo(() => {
    const fromSlug = labelForTemuShopTypeSlug(shopTypeSlug);
    if (fromSlug) return fromSlug;
    return normalizeTemuShopType(workspaceAuth?.workspaceShopType) || '';
  }, [shopTypeSlug, workspaceAuth?.workspaceShopType]);

  const scopedShops = useMemo(() => {
    const list = (shops || []).filter((s) => isTemuShopAuthorized(s));
    if (!expectedType) return list;
    return list.filter((s) => shopTypeEqualsTemu(s.shopType, expectedType));
  }, [shops, expectedType]);

  const canUseAllShops = scopedShops.length >= 2;

  const loadShops = useCallback(async () => {
    setLoading(true);
    try {
      const list = await platformAPI.listShops();
      const temuShops = (Array.isArray(list) ? list : []).filter(
        (s) => String(s.platformCode || '').toUpperCase() === 'TEMU',
      );
      setShops(temuShops);
      const storedShopId = readStoredShopId();
      const urlShopId = readUrlShopId();
      const authorizedOfType = temuShops.filter((s) => {
        if (!isTemuShopAuthorized(s)) return false;
        if (!expectedType) return true;
        return shopTypeEqualsTemu(s.shopType, expectedType);
      });
      setActiveShopId((prev) => {
        const candidates = [urlShopId, storedShopId, prev].filter(Boolean);
        for (const candidate of candidates) {
          if (isAllShopsScope(candidate)) {
            if (authorizedOfType.length >= 2) {
              persistShopId(PLATFORM_ALL_SHOPS_ID);
              return PLATFORM_ALL_SHOPS_ID;
            }
            continue;
          }
          const found = temuShops.find((s) => String(s.id) === String(candidate));
          if (found && isTemuShopAuthorized(found)) {
            if (expectedType && !shopTypeEqualsTemu(found.shopType, expectedType)) {
              continue;
            }
            persistShopId(String(candidate));
            return String(candidate);
          }
        }
        if (!temuShops.length && (storedShopId || prev)) {
          return String(storedShopId || prev);
        }
        const sameType = temuShops.find((s) => (
          isTemuShopAuthorized(s)
          && (!expectedType || shopTypeEqualsTemu(s.shopType, expectedType))
        ));
        if (sameType) {
          persistShopId(String(sameType.id));
          return String(sameType.id);
        }
        persistShopId('');
        return '';
      });
    } catch (err) {
      setShops([]);
      const msg = String(err?.message || '').trim();
      if (msg) showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [expectedType]);

  useEffect(() => {
    loadShops();
  }, [loadShops, workspaceMode]);

  const switchToAll = useCallback(() => {
    if (scopedShops.length < 2) {
      return { ok: false, message: '同类型已授权店铺不足 2 家，无法进入全部视图' };
    }
    if (isAllShopsScope(activeShopId)) {
      return { ok: true, unchanged: true };
    }
    setActiveShopId(PLATFORM_ALL_SHOPS_ID);
    persistShopId(PLATFORM_ALL_SHOPS_ID);
    return { ok: true, all: true };
  }, [scopedShops.length, activeShopId]);

  const switchShop = useCallback((shopId) => {
    if (isAllShopsScope(shopId)) {
      return switchToAll();
    }
    const shop = shops.find((s) => String(s.id) === String(shopId));
    if (!shop) {
      return { ok: false, message: '店铺不存在' };
    }
    if (!isTemuShopAuthorized(shop)) {
      return { ok: false, message: '只能切换已授权的店铺' };
    }
    const current = shops.find((s) => String(s.id) === String(activeShopId));
    const currentType = normalizeTemuShopType(current?.shopType)
      || expectedType
      || fixedUi("半托管");
    if (!shopTypeEqualsTemu(shop.shopType, currentType)) {
      const targetType = normalizeTemuShopType(shop.shopType) || '未知';
      return {
        ok: false,
        message: `只能切换同类型店铺（当前「${currentType}」，目标「${targetType}」）。跨类型请返回门禁重进。`,
      };
    }
    const next = String(shopId);
    if (next === String(activeShopId)) {
      return { ok: true, unchanged: true, shop };
    }
    setActiveShopId(next);
    persistShopId(next);
    return { ok: true, shop };
  }, [shops, activeShopId, expectedType, switchToAll]);

  const isAllShopsMode = isAllShopsScope(activeShopId);

  const activeShop = useMemo(
    () => (isAllShopsMode
      ? null
      : (shops.find((s) => String(s.id) === String(activeShopId)) || null)),
    [shops, activeShopId, isAllShopsMode],
  );

  const value = useMemo(
    () => ({
      shops,
      scopedShops,
      canUseAllShops,
      activeShopId,
      activeShop,
      isAllShopsMode,
      expectedShopType: expectedType,
      loading,
      switchShop,
      switchToAll,
      reloadShops: loadShops,
    }),
    [
      shops,
      scopedShops,
      canUseAllShops,
      activeShopId,
      activeShop,
      isAllShopsMode,
      expectedType,
      loading,
      switchShop,
      switchToAll,
      loadShops,
    ],
  );

  return (
    <TemuShopContext.Provider value={value}>
      {children}
    </TemuShopContext.Provider>
  );
}

export function useTemuShop() {
  const ctx = useContext(TemuShopContext);
  if (!ctx) {
    throw new Error('useTemuShop must be used within TemuShopProvider');
  }
  return ctx;
}

export function useTemuShopOptional() {
  return useContext(TemuShopContext);
}
